import BaseService from './baseService';

export default class SnowflakeLoadConfigService extends BaseService {
    constructor() {
        super('snowflake-load-config');
    }

    /**
     * Retrieves the load configuration details for the specified configuration ID.
     * @param {number} configId - The id of the load configuration to retrieve. Must be a positive integer.
     * @returns Load configuration
     */
    getConfig = (configId: number) => this.httpGetRequest(`${configId}`);

    /**
    * Gets a list of Snowflake load configurations based on the provided filter criteria.
    * @param {Object} filters - Request object for retrieving a list of Snowflake load configurations.
    * @param {Array<number>} filters.ids - Array of Snowflake load configuration IDs to filter by.
    * @param {Array<string>} filters.names - Array of Snowflake load configuration names to filter by.
    * @param {Array<number>} filters.releaseGroupIds - Release group IDs to filter by.
    * @param {Array<number>} filters.taskIds - Task IDs to filter by.
    * @param {boolean} filters.includeGlobal - Indicates whether to include global configurations (not bound to any specific task).
    * @param {Array<number>} filters.associatedScenarioIds - Associated scenario IDs to filter by.
    * @param {Array<number>} filters.associatedTaskIds -  Associated task IDs to filter by.
    * @returns Snowflake configurations list
    */
    list = (filters: Partial<{
        ids: number[];
        names: string[];
        releaseGroupIds: number[];
        taskIds: number[];
        includeGlobal: boolean;
        associatedScenarioIds: number[];
        associatedTaskIds: number[];
    }> = {}) => this.httpGetRequest(`list?${this.generateQueryString(filters)}`);

    /**
     * Creates a Snowflake load configuration based on the provided request.
     * @param {Object} request SnowflakeLoad Config Upsert Request.
     * @param {number | null} request.id - Load configuration ID. If null, a new configuration will be created; If provided, the existing configuration will be updated.
     * @param {string} request.name - Name of the load configuration.
     * @param {number} request.releaseGroupId - Release group ID associated with this load configuration.
     * @param {number} request.taskId - Task ID associated with this load configuration.
     * @param {Array<number>} request.tableIds - Table IDs to be associated with this load configuration.
     * @returns New config id
     */
    createConfig = (request: {
        id: number | null;
        name: string;
        releaseGroupId: number;
        taskId: number;
        tableIds: number[]
    }) => this.httpPostRequest('', request);

    /**
     * Updates a Snowflake load configuration based on the provided request.
     * @param {Object} request SnowflakeLoad Config Upsert Request.
     * @param {number} request.id - Load configuration ID. If null, a new configuration will be created; If provided, the existing configuration will be updated.
     * @param {string} request.name - Name of the load configuration.
     * @param {number} request.releaseGroupId - Release group ID associated with this load configuration.
     * @param {number} request.taskId - Task ID associated with this load configuration.
     * @param {Array<number>} request.tableIds - Table IDs to be associated with this load configuration.
     */
    updateConfig = (request: {
        id: number;
        name: string;
        releaseGroupId: number;
        taskId: number;
        tableIds: number[];
    }) => this.httpPutRequest('', request);

    /**
     * Deletes Snowflake load configurations based on the provided IDs. 
     * @param {Array<number>} configIds - The IDs of the load configurations to delete.
     */
    deleteConfigs = (configIds: number[]) => this.httpDeleteRequest(`?${this.generateQueryString({ id: configIds })}`);
}